import { CheckIcon, XIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { useReviewBriefAdUnit } from '@/hooks/useBriefWorkspace';
import type { ConceptCopyVariant } from '@/lib/concept-copy/types';
import { ITEM_META } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

type ReviewStatus = ConceptCopyVariant['status'];

/**
 * Same states and tones as the Brief's ad-copy card (`BriefConceptAdUnits`:
 * draft neutral, approved success, rejected destructive, exported neutral).
 * "Draft" reads "Not reviewed" here because that is the question this control
 * answers.
 */
export const REVIEW_STATE: Record<ReviewStatus, { label: string; tone: StatusTone }> = {
  draft: { label: 'Not reviewed', tone: 'neutral' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'destructive' },
  exported: { label: 'Exported', tone: 'neutral' },
};

const APPROVE_CLASS =
  'gap-1.5 border border-success/30 bg-success/10 text-success hover:bg-success/15 hover:text-foreground';
const REJECT_CLASS = 'gap-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive';

/**
 * A SEPARATE COMPONENT, and that is load-bearing rather than tidiness.
 * `useReviewBriefAdUnit` reaches `useAuth` through `useResolvedOwnerId`, so
 * calling it in the parent would demand an AuthProvider from every mount of
 * the Content section, including the ones that never review anything.
 */
export function AdUnitReviewControls({
  briefId,
  adUnitId,
  status,
  onReviewed,
  onError,
}: {
  briefId: string;
  adUnitId: string;
  status: ReviewStatus;
  onReviewed: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const review = useReviewBriefAdUnit(briefId);
  const state = REVIEW_STATE[status];

  const run = (next: 'approved' | 'rejected') =>
    review.mutate(
      { adUnitId, status: next },
      {
        onSuccess: onReviewed,
        onError: (cause) =>
          onError(cause instanceof Error ? cause.message : 'That review did not go through.'),
      },
    );

  return (
    <div className="flex flex-col gap-2" data-review-status={status}>
      <div className="flex items-center justify-between gap-2">
        <StatusBadge
          kind="generic"
          tone={state.tone}
          icon={
            status === 'approved' ? (
              <CheckIcon aria-hidden weight="bold" className="size-3" />
            ) : status === 'rejected' ? (
              <XIcon aria-hidden weight="bold" className="size-3" />
            ) : undefined
          }
        >
          {state.label}
        </StatusBadge>
        <span className={ITEM_META}>This option</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="flat"
          size="compact"
          className={cn(APPROVE_CLASS)}
          disabled={review.isPending || status === 'approved'}
          onClick={() => run('approved')}
        >
          <CheckIcon aria-hidden weight="bold" className="size-3.5" />
          Approve
        </Button>
        <Button
          type="button"
          variant="flat"
          size="compact"
          className={cn(REJECT_CLASS)}
          disabled={review.isPending || status === 'rejected'}
          onClick={() => run('rejected')}
        >
          <XIcon aria-hidden weight="bold" className="size-3.5" />
          Reject
        </Button>
      </div>
    </div>
  );
}
